/** Defines and applies responsive filters for "Overdue", "Upcoming", and "Unscheduled" task categories. */
import { useMemo } from 'react';
import { useBreakpoint, breakpointOrder } from '@/hooks/ui/useBreakpoint';
import {
  filterOverdueTasks,
  filterUpcomingTasks,
  filterUnscheduledTasks,
} from '@/features/tasks/utils/views/filtersViewUtils';

/**
 * Centralises the filter definitions for the "Overdue", "Upcoming" and
 * "Unscheduled" task sections shown in the RightPanel.
 *
 * The overdue definition is responsive: on small screens a task is overdue when
 * its scheduled end-time has passed by more than an hour; on larger screens a
 * task is overdue when its date is before today (midnight comparison).
 *
 * @param {Array<Object>} tasks - The filtered task list from useTasksContext.
 * @param {Map<string, Object>} tasksMap
 * @returns {{
 *   overdueTasks: Array<Object>,
 *   upcomingTasks: Array<Object>,
 *   unscheduledTasks: Array<Object>,
 * }}
 */
export const useTaskFilters = (tasks, tasksMap = new Map()) => {
  const currentBreakpoint = useBreakpoint();
  const isSmallScreen = breakpointOrder[currentBreakpoint] <= breakpointOrder.s;

  const overdueTasks = useMemo(
    () => filterOverdueTasks(tasks, isSmallScreen, tasksMap),
    [tasks, isSmallScreen, tasksMap],
  );

  const upcomingTasks = useMemo(
    () => filterUpcomingTasks(tasks, tasksMap),
    [tasks, tasksMap],
  );

  const unscheduledTasks = useMemo(
    () => filterUnscheduledTasks(tasks, tasksMap),
    [tasks, tasksMap],
  );

  return { overdueTasks, upcomingTasks, unscheduledTasks };
};
